import { useEffect, useMemo, useRef, useState, type ClipboardEvent, type KeyboardEvent } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import type { ImportCommitResult, ImportRow, ImportValidation } from '@dru/types';
import { Alert, Button, Checkbox, Dialog, IconAction, KeyFigures, RowActions, SearchSelect, SelectField, Steps, TextField, cn } from '@dru/ui';
import { CircleCheck, Copy, Download, FileSpreadsheet, Plus, Search, Settings2, Trash2, TriangleAlert, Upload } from 'lucide-react';
import { api } from '../../../lib/api';
import { errorMessage } from '../../../lib/auth';
import { useQuery } from '../../../lib/useQuery';
import { PageHeader } from '../../../layouts/ConsoleShell';
import { TEMPLATE_COLUMNS, defaultOptionalColumns, friendlyImportError, rowsAsCsv, templateColumns } from './employee-import-utils';
import { BLOOD_GROUPS } from '@dru/types';
import './employee-import.css';

type Options = {
  company: { id: string; name: string };
  plants: Array<{ id: string; code: string; name: string }>;
  departments?: Array<{ id: string; code: string; name: string; plantId: string }>;
  preference: { mode: 'EXISTING' | 'AUTOMATIC' | null; prefix: string | null };
};
type ExistingIdentity = { id: string; employeeCode: string; fullName: string; mobile: string | null; email: string | null };
type Draft = { key: string; values: Record<string, string>; edited: string[] };
// The review grid, in the template's order (owner, 26 Oct 2026). Email,
// employment type and joining date are optional: their column appears only
// when the uploaded file has something in it.
const ALL_FIELDS = [
  ['employeeCode', 'Employee ID'], ['fullName', 'Full Name'], ['mobile', 'Mobile'],
  ['sex', 'Gender'], ['dateOfBirth', 'DOB'], ['bloodGroup', 'Blood Group'], ['plantId', 'Plant'],
  ['email', 'Email'], ['employmentType', 'Employment Type'], ['joinedOn', 'Joining Date'],
  ['designation', 'Designation'], ['departmentCode', 'Department'], ['natureOfWork', 'Nature of work'],
] as const;
type FieldKey = typeof ALL_FIELDS[number][0];
const SHOWN_ONLY_IF_FILLED: readonly FieldKey[] = ['email', 'employmentType', 'joinedOn', 'designation', 'departmentCode', 'natureOfWork'];
const errorField = (key: string) => key === 'joinedOn' ? 'joining_date' : key.replace(/[A-Z]/g, c => `_${c.toLowerCase()}`);
const statusLabel = (row?: ImportRow) => row?.outcome === 'VALID' ? 'Ready' : row?.outcome === 'DUPLICATE' ? 'Already exists · skipped' : 'Needs a fix';
const rowTone = (row?: ImportRow) => row?.outcome === 'VALID' ? 'valid' : row?.outcome === 'DUPLICATE' ? 'duplicate' : 'invalid';
const normalized = (v: string) => v.normalize('NFKC').trim().replace(/\s+/g, ' ').toLowerCase();
// Older files may still carry a column the app no longer uses; drop it from
// every row so it is never shown, edited or sent back.
const UNUSED_KEYS = ['departmentId'];
const UNUSED_ERROR_FIELDS = ['department_id'];
const withoutUnused = (values: Record<string, string>) => Object.fromEntries(Object.entries(values).filter(([k]) => !UNUSED_KEYS.includes(k)));

export function EmployeeImport() { return <EmployeeImportForm />; }
export function SupportEmployeeImport() {
  const { id } = useParams();
  return <EmployeeImportForm companyId={id} />;
}

export function EmployeeImportForm({ companyId }: { companyId?: string }) {
  const nav = useNavigate();
  const base = companyId ? `/admin/tenants/${companyId}/employee-import` : '/employees/import';
  const options = useQuery<Options>(() => api.get(`${base}/options`), [base]);
  const fileRef = useRef<HTMLInputElement>(null);
  const requestChain = useRef<Promise<unknown>>(Promise.resolve());
  const commitLock = useRef(false);
  const versionRef = useRef(0);
  const [stage, setStage] = useState<'upload' | 'review' | 'done'>('upload');
  const [rows, setRows] = useState<Draft[]>([]);
  const onePlant = options.data?.plants.length === 1;
  const fields = useMemo(() => ALL_FIELDS.filter(([k]) =>
    !(k === 'plantId' && onePlant)
    && (!SHOWN_ONLY_IF_FILLED.includes(k) || rows.some((r) => (r.values[k] ?? '').trim() !== ''))), [rows, onePlant]);
  // The optional columns a downloaded template carries; chosen before download.
  const [templateFor, setTemplateFor] = useState<null | 'excel' | 'csv'>(null);
  const [optionalCols, setOptionalCols] = useState<Set<string>>(defaultOptionalColumns);
  const [validation, setValidation] = useState<ImportValidation | null>(null);
  const [version, setVersion] = useState(0);
  const [validatedVersion, setValidatedVersion] = useState(0);
  const [result, setResult] = useState<ImportCommitResult | null>(null);
  const [busy, setBusy] = useState(false);
  const [checking, setChecking] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [filename, setFilename] = useState('');
  const [defaultPlant, setDefaultPlant] = useState('');
  const [search, setSearch] = useState('');
  const [filter, setFilter] = useState('all');
  const [page, setPage] = useState(0);
  const [existing, setExisting] = useState<ExistingIdentity[] | null>(null);
  const [reviewingExisting, setReviewingExisting] = useState(false);
  const [confirming, setConfirming] = useState(false);
  const [skipInvalid, setSkipInvalid] = useState(false);
  const [idMode, setIdMode] = useState('AUTOMATIC');
  /** Set when the first upload wrote the company's ID setting, so the review step can say so. */
  const [idDefaulted, setIdDefaulted] = useState<string | null>(null);
  const [prefix, setPrefix] = useState('');
  const [idSaved, setIdSaved] = useState(false);
  const data = options.data;
  const pending = version !== validatedVersion || checking;
  const summary = validation?.summary;
  const dirty = stage === 'review' && rows.length > 0;

  useEffect(() => {
    if (data?.plants.length === 1) setDefaultPlant(data.plants[0]!.id);
    // Advanced options open on the company's saved setting.
    if (data?.preference.mode) { setIdMode(data.preference.mode); setPrefix(data.preference.prefix ?? ''); }
  }, [data]);
  // One plant: every row goes there, so the grid does not ask.
  const singlePlant = data?.plants.length === 1 ? data.plants[0]! : null;

  useEffect(() => {
    if (!dirty) return;
    const before = (e: BeforeUnloadEvent) => { e.preventDefault(); e.returnValue = ''; };
    const click = (e: MouseEvent) => {
      const link = (e.target as HTMLElement).closest('a[href]') as HTMLAnchorElement | null;
      if (!link || link.download || link.target === '_blank' || link.href === window.location.href) return;
      if (!window.confirm('Leave this preview? Your employees have not been imported.')) { e.preventDefault(); e.stopPropagation(); }
    };
    const back = () => { if (!window.confirm('Leave this preview? Your employees have not been imported.')) window.history.go(1); };
    window.addEventListener('beforeunload', before);
    document.addEventListener('click', click, true);
    window.addEventListener('popstate', back);
    return () => { window.removeEventListener('beforeunload', before); document.removeEventListener('click', click, true); window.removeEventListener('popstate', back); };
  }, [dirty]);

  // Serialize requests: a slow validation can never overwrite a more recent edit on the server.
  useEffect(() => {
    if (stage !== 'review' || !validation || version === validatedVersion || !rows.length) return;
    let active = true;
    const snapshot = rows.map(r => r.values);
    const batchId = validation.batchId;
    const timer = window.setTimeout(() => {
      setChecking(true);
      requestChain.current = requestChain.current.catch(() => undefined).then(async () => {
        if (!active) return;
        try {
          const checked = await api.post<ImportValidation>(`${base}/${batchId}/review`, { rows: snapshot });
          if (active && versionRef.current === version) { setValidation(checked); setValidatedVersion(version); setError(null); }
        } catch (e) { if (active) setError(errorMessage(e)); }
        finally { if (active) setChecking(false); }
      });
    }, 450);
    return () => { active = false; window.clearTimeout(timer); };
  }, [rows, version, validatedVersion, stage, validation?.batchId, base]);

  function changed(next: Draft[]) {
    versionRef.current += 1;
    setVersion(versionRef.current); setRows(next); setConfirming(false); setSkipInvalid(false); setChecking(false);
  }
  function edit(index: number, field: string, value: string) {
    changed(rows.map((r, i) => i === index ? {
      ...r, values: { ...r.values, [field]: value,
        ...(field === 'plantId' ? { plantCode: '' } : {}),
      }, edited: [...new Set([...r.edited, field])],
    } : r));
  }
  async function upload(file: File) {
    if (busy) return;
    setBusy(true); setError(null);
    try {
      if (!/\.(xlsx|csv)$/i.test(file.name)) throw new Error('Choose an Excel (.xlsx) or CSV (.csv) file.');
      if (file.size > 1_000_000) throw new Error('Maximum file size is 1 MB.');
      // A company importing for the first time has no ID setting yet and the
      // server refuses to check a file without one. Automatic is the default;
      // Advanced options holds the alternative.
      if (data && !data.preference.mode) {
        await api.post(`${base}/preference`, { mode: idMode, ...(idMode === 'AUTOMATIC' && prefix ? { prefix } : {}) });
        options.reload();
        // A company-wide setting was just written: said, not done silently.
        setIdDefaulted(idMode);
      }
      let content: string;
      const excel = /\.xlsx$/i.test(file.name);
      if (excel) {
        const bytes = new Uint8Array(await file.arrayBuffer());
        let binary = ''; for (const byte of bytes) binary += String.fromCharCode(byte);
        content = btoa(binary);
      } else content = await file.text();
      const checked = await api.post<ImportValidation>(`${base}/validate`, {
        filename: file.name, content, encoding: excel ? 'base64' : 'utf8', ...(defaultPlant ? { defaultPlantId: defaultPlant } : {}),
      });
      setFilename(file.name); setValidation(checked);
      const carriedUnused = checked.preview.some(r => UNUSED_KEYS.some(k => r.values?.[k]));
      // A plant with exactly one department: every row of it without a
      // department goes there, so HR is never asked for it. Several
      // departments, or none, and the row is left as the file said.
      let filledDept = false;
      const drafts = checked.preview.map(r => {
        const values = withoutUnused(r.values ?? {});
        if (!(values.departmentCode ?? '').trim()) {
          const plantOf = values.plantId || defaultPlant || (data?.plants.length === 1 ? data.plants[0]!.id : '');
          const depts = (data?.departments ?? []).filter((d) => d.plantId === plantOf);
          if (plantOf && depts.length === 1) { values.departmentCode = depts[0]!.name; filledDept = true; }
        }
        return { key: crypto.randomUUID(), values, edited: [] as string[] };
      });
      setRows(drafts);
      // If the file carried an unused column, or a department was filled in,
      // check the rows again so the server sees what the grid shows.
      versionRef.current = carriedUnused || filledDept ? 1 : 0; setVersion(versionRef.current); setValidatedVersion(0); setStage('review'); setPage(0);
    } catch (e) { setError(errorMessage(e)); }
    finally { setBusy(false); if (fileRef.current) fileRef.current.value = ''; }
  }
  async function configureIds() {
    setBusy(true); setError(null);
    try { await api.post(`${base}/preference`, { mode: idMode, ...(idMode === 'AUTOMATIC' && prefix ? { prefix } : {}) }); options.reload(); setIdSaved(true); }
    catch (e) { setError(errorMessage(e)); } finally { setBusy(false); }
  }
  function download(blob: Blob, name: string) {
    const url = URL.createObjectURL(blob); const a = document.createElement('a');
    a.href = url; a.download = name; a.click(); window.setTimeout(() => URL.revokeObjectURL(url), 1000);
  }
  async function template(excel: boolean) {
    setError(null);
    try {
      const cols = templateColumns(optionalCols);
      const headers = cols.map((c) => c.header);
      const example = cols.map((c) => (c.key === 'employeeCode' && data?.preference.mode === 'AUTOMATIC' ? '' : c.example));
      if (!excel) return download(new Blob([rowsAsCsv([headers, example])], { type: 'text/csv;charset=utf-8' }), 'employee-template.csv');
      const { default: ExcelJS } = await import('exceljs');
      const book = new ExcelJS.Workbook(); const sheet = book.addWorksheet('Employees');
      sheet.addRow(headers); sheet.addRow(example);
      sheet.getRow(1).font = { bold: true, color: { argb: 'FFFFFFFF' } };
      sheet.getRow(1).fill = { type: 'pattern', pattern: 'solid', fgColor: { argb: 'FF0F766E' } };
      sheet.columns.forEach((c, i) => { c.width = cols[i]?.key === 'fullName' ? 25 : 18; c.numFmt = '@'; });
      sheet.views = [{ state: 'frozen', ySplit: 1 }];
      const letter = (key: string) => { const i = cols.findIndex((c) => c.key === key); return i < 0 ? null : String.fromCharCode(65 + i); };
      const lists: Array<[string, string]> = [['sex', '"MALE,FEMALE,OTHER"'], ['bloodGroup', `"${BLOOD_GROUPS.join(',')}"`], ['employmentType', '"DIRECT,CONTRACTOR"']];
      for (const [key, list] of lists) {
        const col = letter(key); if (!col) continue;
        for (let r = 2; r <= 501; r++) sheet.getCell(`${col}${r}`).dataValidation = { type: 'list', allowBlank: true, formulae: [list] };
      }
      const dob = letter('dateOfBirth'); if (dob) sheet.getCell(`${dob}1`).note = 'Required. Use YYYY-MM-DD, e.g. 1992-03-15.';
      const sex = letter('sex'); if (sex) sheet.getCell(`${sex}1`).note = 'MALE, FEMALE or OTHER.';
      const blood = letter('bloodGroup'); if (blood) sheet.getCell(`${blood}1`).note = 'Optional. Leave blank when unknown.';
      const bytes = await book.xlsx.writeBuffer();
      download(new Blob([bytes as BlobPart], { type: 'application/vnd.openxmlformats-officedocument.spreadsheetml.sheet' }), 'employee-template.xlsx');
    } catch (e) { setError(errorMessage(e)); }
  }
  async function commit() {
    if (!validation || pending || busy || commitLock.current || !rows.length) return;
    commitLock.current = true; setBusy(true); setError(null);
    try {
      const outcome = await api.post<ImportCommitResult>(`${base}/${validation.batchId}/commit`, { revision: validation.revision, skipInvalid });
      setResult(outcome); setStage('done'); setConfirming(false);
      try { const report = await api.get<{ rows: ImportRow[] }>(`${base}/${validation.batchId}`); setValidation(v => v ? { ...v, preview: report.rows } : v); } catch { /* original validated errors remain downloadable */ }
    } catch (e) { setError(errorMessage(e)); setConfirming(false); }
    finally { commitLock.current = false; setBusy(false); }
  }
  async function reviewExisting(row: Draft) {
    setReviewingExisting(true); setError(null);
    try {
      const query = new URLSearchParams();
      if (row.values.employeeCode) query.set('code', row.values.employeeCode);
      if (row.values.mobile) query.set('mobile', row.values.mobile);
      if (row.values.email) query.set('email', row.values.email);
      const response = await api.get<{ records: ExistingIdentity[] }>(`${base}/existing?${query}`);
      setExisting(response.records);
    } catch (e) { setError(errorMessage(e)); }
    finally { setReviewingExisting(false); }
  }
  function errorsCsv() {
    const bad = validation?.preview.filter(r => !['VALID', 'COMMITTED'].includes(r.outcome)) ?? [];
    download(new Blob([rowsAsCsv([
      ['Row', 'Employee ID', 'Full Name', 'Mobile', 'Status', 'Correction'],
      ...bad.map(r => [r.rowNumber, r.values?.employeeCode, r.values?.fullName, r.values?.mobile, statusLabel(r), (r.errors ?? []).map(e => friendlyImportError(e.field, e.message)).join('; ')]),
    ])], { type: 'text/csv;charset=utf-8' }), 'employee-import-errors.csv');
  }
  function reset() {
    if (dirty && !window.confirm('Discard this employee preview and choose another file?')) return;
    setStage('upload'); setRows([]); setValidation(null); setResult(null); setError(null); setSearch(''); setFilter('all'); setPage(0); setSkipInvalid(false); setConfirming(false);
  }
  const filtered = useMemo(() => rows.map((row, index) => ({ row, index })).filter(({ row, index }) => {
    const outcome = validation?.preview[index]?.outcome;
    const q = search.trim().toLowerCase();
    return (filter === 'all' || outcome === filter) && (!q || Object.values(row.values).some(v => v.toLowerCase().includes(q)));
  }), [rows, search, filter, validation]);
  const pageCount = Math.max(1, Math.ceil(filtered.length / 25));
  const visible = filtered.slice(Math.min(page, pageCount - 1) * 25, (Math.min(page, pageCount - 1) + 1) * 25);

  function paste(e: ClipboardEvent<HTMLInputElement>, index: number, fieldIndex: number) {
    const text = e.clipboardData.getData('text/plain');
    if (!/[\t\n]/.test(text)) return;
    e.preventDefault();
    if (filter !== 'all' || search) { setError('Clear search and filters before pasting multiple cells.'); return; }
    const grid = text.replace(/\r/g, '').replace(/\n$/, '').split('\n').map(line => line.split('\t'));
    if (rows.length + grid.length > 10_000 || text.length > 1_000_000) { setError('Paste exceeds the import limit.'); return; }
    const next = rows.map(r => ({ ...r, values: { ...r.values }, edited: [...r.edited] }));
    grid.forEach((line, offset) => {
      const target = index + offset;
      if (!next[target]) next[target] = { key: crypto.randomUUID(), values: { plantId: defaultPlant }, edited: [] };
      line.forEach((value, col) => {
        const f = fields[fieldIndex + col]?.[0]; if (!f) return;
        const record = next[target]!;
        if (f === 'plantId') { record.values.plantId = data?.plants.find(p => normalized(p.name) === normalized(value) || normalized(p.code) === normalized(value))?.id ?? ''; record.values.plantCode = value; }
        else record.values[f] = value;
        record.edited.push(f);
      });
    });
    changed(next);
  }
  function keyNavigate(e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>, index: number, field: string) {
    if (e.currentTarget instanceof HTMLSelectElement || !['ArrowUp', 'ArrowDown', 'Enter'].includes(e.key)) return;
    const next = e.key === 'ArrowUp' ? index - 1 : index + 1;
    const target = document.querySelector<HTMLElement>(`[data-import-row="${next}"] [data-field="${field}"]`);
    if (target) { e.preventDefault(); target.focus(); }
  }
  function cell(index: number, field: FieldKey, label: string, columnIndex: number) {
    const row = rows[index]!;
    const value = row.values[field] ?? '';
    const issues = pending ? [] : (validation?.preview[index]?.errors ?? []).filter(e => e.field === errorField(field) || (field === 'plantId' && e.field === 'plant_code'));
    const common = { 'aria-label': `${label}, row ${index + 1}`, 'aria-invalid': issues.length > 0, 'aria-describedby': issues.length ? `error-${row.key}-${field}` : undefined, 'data-field': field, disabled: busy,
      onKeyDown: (e: KeyboardEvent<HTMLInputElement | HTMLSelectElement>) => keyNavigate(e, index, field),
      className: `import-cell ${row.edited.includes(field) ? 'import-edited' : ''} ${issues.length ? 'import-invalid' : ''}` };
    let choices: Array<[string, string]> | null = null;
    let selected = value;
    if (field === 'sex') { choices = [['', 'Not recorded'], ['MALE', 'Male'], ['FEMALE', 'Female'], ['OTHER', 'Other']]; selected = ['M', 'F', 'O'].includes(value.toUpperCase()) ? ({ M: 'MALE', F: 'FEMALE', O: 'OTHER' }[value.toUpperCase()] ?? value) : value.toUpperCase(); }
    if (field === 'employmentType') { choices = [['', 'Direct (default)'], ['DIRECT', 'Direct'], ['CONTRACTOR', 'Contractor'], ['APPRENTICE', 'Apprentice']]; selected = value.toUpperCase() === 'CONTRACT' ? 'CONTRACTOR' : value.toUpperCase(); }
    if (field === 'bloodGroup') { choices = [['', 'Not recorded'], ...BLOOD_GROUPS.map((g) => [g, g] as [string, string])]; selected = value.toUpperCase().replace(/\s+/g, ''); }
    if (field === 'plantId') choices = [['', 'Choose plant'], ...(data?.plants.map(p => [p.id, p.name] as [string, string]) ?? [])];
    if (field === 'departmentCode') {
      // The departments of this row's plant; the server matches by name or code.
      const plantOf = row.values.plantId || defaultPlant || (data?.plants.length === 1 ? data.plants[0]!.id : '');
      const depts = (data?.departments ?? []).filter((d) => d.plantId === plantOf);
      if (depts.length) choices = [['', 'Not recorded'], ...depts.map((d) => [d.name, d.name] as [string, string])];
    }
    return <div className="import-field"><label className="import-mobile-label">{label}</label>
      {choices ? <select {...common} value={selected} onChange={e => edit(index, field, e.target.value)}>{!choices.some(([v]) => v === selected) && <option value={selected}>{selected || 'Choose'}</option>}{choices.map(([v, name]) => <option key={v} value={v}>{name}</option>)}</select>
        : <input {...common} value={value} placeholder={field === 'employeeCode' && data?.preference.mode === 'AUTOMATIC' ? 'Automatic' : field === 'dateOfBirth' || field === 'joinedOn' ? 'YYYY-MM-DD' : ''} inputMode={field === 'mobile' ? 'tel' : undefined} onChange={e => edit(index, field, e.target.value)} onPaste={e => paste(e, index, columnIndex)} />}
      {issues.length > 0 && <span id={`error-${row.key}-${field}`} className="import-cell-error">{issues.map(e => friendlyImportError(e.field, e.message)).join('. ')}</span>}
    </div>;
  }

  const stepState = (k: 'upload' | 'review' | 'done') => {
    const order = ['upload', 'review', 'done'];
    const at = order.indexOf(stage), me = order.indexOf(k);
    return me < at ? 'done' as const : me === at ? (k === 'done' ? 'done' as const : 'current' as const) : 'upcoming' as const;
  };
  const autoIds = (data?.preference.mode ?? idMode) === 'AUTOMATIC';
  const shownPrefix = data?.preference.prefix || prefix || 'EMP';
  const counts = [
    { key: 'all', label: 'Total', value: summary?.total ?? rows.length, tone: 'neutral', icon: FileSpreadsheet },
    { key: 'VALID', label: 'Valid', value: summary?.valid ?? 0, tone: 'valid', icon: CircleCheck },
    { key: 'DUPLICATE', label: 'Duplicates', value: summary?.duplicate ?? 0, tone: 'duplicate', icon: Copy },
    { key: 'INVALID', label: 'Errors', value: summary?.invalid ?? 0, tone: 'invalid', icon: TriangleAlert },
  ] as const;

  return <div className="space-y-5 employee-import">
    <PageHeader title="Import employees" hint="Upload your spreadsheet, check and correct the rows, then import. Nothing is saved until you confirm." />
    <Steps orientation="horizontal" label="Import progress" steps={[
      { key: 'upload', label: 'Upload file', state: stepState('upload') },
      { key: 'review', label: 'Review & edit', state: stepState('review') },
      { key: 'done', label: 'Import', state: stepState('done') },
    ]} />
    {companyId && <Alert tone="info" title={`Support-assisted import${data?.company ? ` · ${data.company.name}` : ''}`}>Company context: {data?.company?.name ?? companyId}. Your actions are recorded in the audit log.</Alert>}
    {error && <Alert tone="danger" title="Please check">{error}</Alert>}
    {options.loading && !data && <p role="status" className="text-small text-neutral-600">Loading your company and plants…</p>}
    {options.error && <Alert tone="danger">{options.error}<Button variant="secondary" onClick={options.reload}>Retry</Button></Alert>}
    {data && !data.plants.length && <Alert tone="warning" title="Plant access needs to be configured">Ask your company administrator or support team to configure your plant access before importing employees.</Alert>}

    {/* ── Step 1: upload ─────────────────────────────────── */}
    {data && data.plants.length > 0 && stage === 'upload' && <div className="import-panel space-y-6">
      <label className={cn('import-drop', busy || (data.plants.length > 1 && !defaultPlant) ? 'opacity-60' : 'cursor-pointer')}>
        <span className="import-drop-icon" aria-hidden><Upload className="h-6 w-6" /></span>
        <span className="text-body-lg font-semibold text-neutral-900">{busy ? 'Reading your file…' : 'Choose your Excel or CSV file'}</span>
        <span className="text-small text-neutral-600">
          .xlsx or .csv, up to 1 MB{singlePlant ? <> · employees are added to <strong className="font-medium text-neutral-800">{singlePlant.name}</strong></> : null}
        </span>
        <input ref={fileRef} className="sr-only" aria-label="Upload employee file" type="file" accept=".xlsx,.csv" disabled={busy || (data.plants.length > 1 && !defaultPlant)} onChange={e => { const file = e.target.files?.[0]; if (file) void upload(file); }} />
      </label>
      {busy && <p role="status" className="sr-only">Reading and checking your employees…</p>}

      {data.plants.length > 1 && <SearchSelect label="Add these employees to plant" value={defaultPlant} onChange={setDefaultPlant} placeholder="Choose a plant" searchPlaceholder="Search plants…" options={data.plants.map(p => ({ value: p.id, label: p.name }))} />}

      <div className="import-help">
        <div className="min-w-0 space-y-1">
          <p className="font-semibold text-neutral-900">Don’t have a file yet?</p>
          <p className="text-small text-neutral-600">
            Download the template. Required: <strong>Full name</strong>, <strong>Mobile number</strong>, <strong>Date of birth</strong>.
            {' '}{autoIds ? <>Leave Employee ID blank — we number it for you ({shownPrefix}-…).</> : <>Also fill in each person's <strong>Employee ID</strong>.</>}
          </p>
        </div>
        <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
          <Button variant="secondary" icon={<Download className="h-4 w-4" />} onClick={() => void template(true)}>Download template</Button>
          <button type="button" className="text-small font-medium text-primary-700 hover:underline max-md:min-h-10" onClick={() => setTemplateFor('excel')}>Choose columns</button>
        </div>
      </div>

      <details className="import-advanced">
        <summary><Settings2 className="h-4 w-4" aria-hidden /> Advanced options <span className="font-normal text-neutral-500">· Employee IDs {autoIds ? `generated automatically (${shownPrefix}-…)` : 'taken from your file'}</span></summary>
        <div className="space-y-4 pt-4">
          <SelectField label="Employee IDs" value={idMode} onChange={(v) => { setIdMode(v); setIdSaved(false); }}>
            <option value="AUTOMATIC">Generate automatically (recommended)</option>
            <option value="EXISTING">Use the IDs in my file</option>
          </SelectField>
          {idMode === 'AUTOMATIC' && <TextField label="ID prefix (optional)" value={prefix} onChange={v => { setPrefix(v.toUpperCase()); setIdSaved(false); }} hint="2–8 letters or digits, e.g. ACME. Leave blank to use EMP." />}
          <p className="text-small text-neutral-600">Saved for your company and used by every import. IDs already in a file are always kept.</p>
          <div className="flex items-center gap-3">
            <Button variant="secondary" onClick={configureIds} loading={busy}
                    disabled={(idMode === 'AUTOMATIC' && !!prefix && !/^[A-Z0-9]{2,8}$/.test(prefix))
                      || (data.preference.mode === idMode && (idMode === 'EXISTING' || (prefix || 'EMP') === (data.preference.prefix || 'EMP')))}>
              Save ID setting
            </Button>
            {idSaved && <span role="status" className="text-small text-covered-600">Saved</span>}
          </div>
        </div>
      </details>
    </div>}

    {/* ── Step 2: review & edit ──────────────────────────── */}
    {stage === 'review' && summary && <>
      <div className="flex flex-wrap items-center justify-between gap-3">
        <p className="flex min-w-0 items-center gap-2 text-body text-neutral-800">
          <FileSpreadsheet className="h-4 w-4 shrink-0 text-neutral-500" aria-hidden />
          <strong className="truncate font-semibold">{filename}</strong>
          {singlePlant && <span className="text-neutral-500 max-md:hidden">· {singlePlant.name}</span>}
        </p>
        <Button variant="ghost" size="sm" onClick={reset} disabled={busy}>Choose another file</Button>
      </div>
      {idDefaulted && (
        <p className="text-small text-neutral-600" role="status">
          {idDefaulted === 'AUTOMATIC'
            ? <>Employee IDs will be generated automatically{prefix ? ` (${prefix}-…)` : ''}. This is now your company's setting; change it under Advanced options on the upload step.</>
            : <>Your company will use its own employee IDs from the file. Change it under Advanced options on the upload step.</>}
        </p>
      )}

      {/* The summary is also the filter: select a count to see those rows. */}
      <div className="import-counts" role="group" aria-label="Show rows">
        {counts.map(c => {
          const Icon = c.icon;
          return (
            <button key={c.key} type="button" aria-pressed={filter === c.key}
                    className={cn('import-count', `import-count-${c.tone}`, filter === c.key && 'is-active', pending && 'is-pending')}
                    onClick={() => { setFilter(c.key); setPage(0); }}>
              <Icon className="h-4 w-4" aria-hidden />
              <span className="import-count-label">{c.label}</span>
              <span className="import-count-value">{c.value}</span>
            </button>
          );
        })}
      </div>
      <p className="sr-only" role="status">{pending ? 'Checking your changes…' : `${summary.valid} valid, ${summary.duplicate} duplicates, ${summary.invalid} errors`}</p>

      {data && data.plants.length > 1 && <SearchSelect label="Plant for every row" value={defaultPlant} onChange={plantId => { setDefaultPlant(plantId); changed(rows.map(r => ({ ...r, values: { ...r.values, plantId, plantCode: '' }, edited: [...new Set([...r.edited, 'plantId'])] }))); }} placeholder="Choose plant" searchPlaceholder="Search plants…" options={data.plants.map(p => ({ value: p.id, label: p.name }))} />}

      <div className="import-toolbar">
        <label className="import-search">
          <Search className="h-4 w-4 text-neutral-500" aria-hidden />
          <span className="sr-only">Search rows</span>
          <input type="search" value={search} onChange={e => { setSearch(e.target.value); setPage(0); }} placeholder="Search name, mobile or employee ID" />
        </label>
        <span className="text-small text-neutral-600 max-md:hidden" aria-live="polite">{pending ? 'Checking…' : 'Click any cell to edit. Red needs a fix; yellow already exists and is skipped.'}</span>
        <span className="text-caption text-neutral-600 md:hidden" aria-hidden>{pending ? 'Checking…' : 'Tap to edit · red = fix · yellow = skipped'}</span>
        <Button variant="secondary" size="sm" icon={<Plus className="h-4 w-4" />} disabled={busy || rows.length >= 10000} onClick={() => { changed([...rows, { key: crypto.randomUUID(), values: { fullName: '', mobile: '', plantId: defaultPlant }, edited: [] }]); setFilter('all'); setSearch(''); setPage(Math.floor(rows.length / 25)); }}>Add row</Button>
      </div>

      {!rows.length && <Alert tone="info">No rows left. Add a row or choose another file.</Alert>}
      {rows.length > 0 && filtered.length === 0 && <p className="rounded-card border border-dashed border-neutral-300 p-6 text-center text-small text-neutral-600">No rows match. <button type="button" className="font-medium text-primary-700 hover:underline" onClick={() => { setFilter('all'); setSearch(''); }}>Show all rows</button></p>}
      {filtered.length > 0 && <div className="import-table-wrap"><table className="import-table"><caption className="sr-only">Editable employee rows</caption>
        <thead><tr><th scope="col">#</th>{fields.map(([key, label]) => <th scope="col" key={key}>{label}</th>)}<th scope="col">Status</th><th scope="col"><span className="sr-only">Delete</span></th></tr></thead>
        <tbody>{visible.map(({ row, index }) => {
          const r = validation?.preview[index];
          const tone = pending ? 'pending' : rowTone(r);
          return <tr key={row.key} data-import-row={index} className={`import-row-${tone}`}>
            <th scope="row">{index + 1}</th>
            {fields.map(([key, label], col) => <td key={key}>{cell(index, key, label, col)}</td>)}
            <td>
              <span className={`import-status import-status-${tone}`}>{pending ? 'Checking…' : statusLabel(r)}</span>
              {!pending && r?.outcome === 'DUPLICATE' && <button type="button" className="import-review-link" disabled={reviewingExisting} onClick={() => reviewExisting(row)}>See existing record</button>}
              {!pending && (r?.errors ?? []).filter(e => !UNUSED_ERROR_FIELDS.includes(e.field) && !fields.some(([f]) => errorField(f) === e.field || (f === 'plantId' && e.field === 'plant_code'))).map((e, n) => <p key={n} className="import-cell-error">{friendlyImportError(e.field, e.message)}</p>)}
            </td>
            <td><RowActions><IconAction label={`Delete row ${index + 1}${row.values.fullName ? ` (${row.values.fullName})` : ''}`} icon={Trash2} danger disabled={busy} onClick={() => changed(rows.filter((_, i) => i !== index))} /></RowActions></td>
          </tr>;
        })}</tbody></table></div>}

      {pageCount > 1 && <div className="flex flex-wrap items-center gap-3 text-small text-neutral-700">
        <Button size="sm" variant="secondary" disabled={page === 0} onClick={() => setPage(p => p - 1)}>Previous</Button>
        <span>Page {Math.min(page + 1, pageCount)} of {pageCount} · {filtered.length} rows</span>
        <Button size="sm" variant="secondary" disabled={page >= pageCount - 1} onClick={() => setPage(p => p + 1)}>Next</Button>
      </div>}

      <div className="import-actions">
        <div className="min-w-0 text-small text-neutral-600">
          {pending ? 'Checking your changes…' : <>
            <strong className="text-neutral-900">{summary.valid} ready to import.</strong>
            {summary.duplicate > 0 && <> {summary.duplicate} already exist and will be skipped.</>}
            {summary.invalid > 0 && <> {summary.invalid} need a fix first.</>}
          </>}
          {!pending && (summary.invalid > 0 || summary.duplicate > 0) && <> <button type="button" className="font-medium text-primary-700 hover:underline" onClick={errorsCsv}>Download error report</button></>}
        </div>
        <Button disabled={pending || busy || !summary.valid || !rows.length || !!error} onClick={() => setConfirming(true)}>
          Import {pending ? '' : `${summary.valid} `}employee{summary.valid === 1 ? '' : 's'}
        </Button>
      </div>
    </>}

    {templateFor && <Dialog title="Choose the template's columns" onClose={() => setTemplateFor(null)}
      footer={<><Button variant="secondary" onClick={() => setTemplateFor(null)}>Cancel</Button><Button onClick={() => { const excel = templateFor === 'excel'; setTemplateFor(null); void template(excel); }}>Download {templateFor === 'excel' ? 'Excel' : 'CSV'}</Button></>}>
      <div className="space-y-3">
        <p className="text-small text-neutral-600">Always included: {TEMPLATE_COLUMNS.filter((c) => !c.optional).map((c) => c.header).join(', ')}.</p>
        <p className="font-semibold">Also add the columns you have data for:</p>
        <div className="grid gap-x-6 gap-y-2 sm:grid-cols-2">
          {TEMPLATE_COLUMNS.filter((c) => c.optional).map((c) => (
            <Checkbox key={c.key} label={c.header} checked={optionalCols.has(c.key)} onChange={(e) => {
              const on = e.target.checked;
              setOptionalCols((prev) => { const next = new Set(prev); if (on) next.add(c.key); else next.delete(c.key); return next; });
            }} />
          ))}
        </div>
      </div>
    </Dialog>}
    {existing !== null && <Dialog title="Existing employee" onClose={() => setExisting(null)} footer={<Button variant="secondary" onClick={() => setExisting(null)}>Back to review</Button>}><div className="space-y-4"><p>This employee is already in your register, so the row will be skipped. To change their details, open their profile.</p>{existing.length ? existing.map(record => <dl key={record.id} className="rounded-card border border-neutral-200 p-4"><dt className="font-semibold">{record.fullName}</dt><dd>Employee ID: {record.employeeCode}</dd><dd>Mobile: {record.mobile || 'Not recorded'}</dd><dd>Email: {record.email || 'Not recorded'}</dd></dl>) : <p>The same person appears twice in your file, or the existing record is in a plant you cannot see. Delete or correct the duplicate row.</p>}</div></Dialog>}
    {confirming && summary && <Dialog title={`Import ${summary.valid} employee${summary.valid === 1 ? '' : 's'}?`} onClose={() => { if (!busy) setConfirming(false); }}
      footer={<><Button variant="secondary" disabled={busy} onClick={() => setConfirming(false)}>Back to review</Button><Button onClick={commit} loading={busy} disabled={pending || (!!summary.invalid && !skipInvalid)}>Import {summary.valid} employee{summary.valid === 1 ? '' : 's'}</Button></>}>
      <div className="space-y-4">
        <ul className="import-confirm-list">
          <li><CircleCheck className="h-4 w-4 text-covered-600" aria-hidden /><span><strong>{summary.valid}</strong> will be added{singlePlant ? ` to ${singlePlant.name}` : ''}</span></li>
          {summary.duplicate > 0 && <li><Copy className="h-4 w-4 text-dueSoon-600" aria-hidden /><span><strong>{summary.duplicate}</strong> already exist and will be skipped</span></li>}
          {summary.invalid > 0 && <li><TriangleAlert className="h-4 w-4 text-overdue-600" aria-hidden /><span><strong>{summary.invalid}</strong> still need a fix</span></li>}
        </ul>
        <p className="text-small text-neutral-600">{autoIds ? `Blank employee IDs are numbered automatically (${shownPrefix}-…). ` : ''}No appointments or medical cases are created.</p>
        {summary.invalid > 0 && <Checkbox label={`Skip the ${summary.invalid} row${summary.invalid === 1 ? '' : 's'} with errors and import only the valid ones`} checked={skipInvalid} onChange={e => setSkipInvalid(e.target.checked)} />}
      </div>
    </Dialog>}

    {/* ── Step 3: done ───────────────────────────────────── */}
    {stage === 'done' && result && <div className="import-panel space-y-5">
      <div className="flex items-start gap-3">
        <span className="import-done-icon" aria-hidden><CircleCheck className="h-6 w-6" /></span>
        <div>
          <h2 className="text-h3 text-neutral-900">{result.committed} employee{result.committed === 1 ? '' : 's'} imported</h2>
          <p className="text-body text-neutral-600">They are now in your employee list{autoIds ? ', with employee IDs assigned' : ''}.</p>
        </div>
      </div>
      <KeyFigures className="grid-cols-2 lg:grid-cols-4" figures={[{ key: 'created', label: 'Imported', value: result.committed }, { key: 'duplicates', label: 'Duplicates skipped', value: result.duplicates ?? summary?.duplicate ?? 0 }, { key: 'skipped', label: 'Not imported (total)', value: result.skipped ?? (summary?.total ?? 0) - result.committed }, { key: 'failed', label: 'Failed', value: result.failed ?? 0 }]} />
      <div className="flex flex-wrap gap-3">
        <Button onClick={() => nav(companyId ? `/admin/companies/${companyId}?tab=workforce` : '/hr/employees')}>View employees</Button>
        <Button variant="secondary" onClick={reset}>Import another file</Button>
        {(result.skipped ?? 0) > 0 && <Button variant="ghost" onClick={errorsCsv}>Download error report</Button>}
      </div>
    </div>}
  </div>;
}
